/**
 * Data Visualization Module 3 - Operational Performance Investigation
 *
 * Scenario: The Operations team wants to identify which cities offer the best
 * opportunity for expansion. Students investigate demand, revenue, efficiency,
 * and driver performance across cities to make a final strategic recommendation.
 *
 * Available tables
 *   drivers : driver_id, city, signup_date, rating, vehicle_type
 *   trips   : trip_id, driver_id, trip_date, trip_hour, trip_distance_km,
 *             trip_duration_min, fare_amount, passenger_rating, completed
 *
 * Join: trips JOIN drivers ON driver_id
 *
 * Chapter 1 - Demand Analysis         : Q1-Q3
 * Chapter 2 - Revenue Analysis        : Q4-Q6
 * Chapter 3 - Efficiency & Synthesis  : Q7-Q10
 */

export const DATA_VIZ_LEVELS = [
    {
        id: 1,
        title: 'Demand Analysis',
        description: 'Understand where and when ride demand is highest',
        color: '#10b981',
    },
    {
        id: 2,
        title: 'Revenue Analysis',
        description: 'Identify which cities and trip types generate the most revenue',
        color: '#3b82f6',
    },
    {
        id: 3,
        title: 'Efficiency & Synthesis',
        description: 'Evaluate driver performance, operational efficiency, and make the expansion call',
        color: '#f97316',
    },
];

export const DATA_VIZ_CHALLENGES = [

    // ═══ Chapter 1 - Demand Analysis ════════════════════════════════
    {
        id: 'city-ride-demand',
        level: 1,
        title: 'Q1: Which cities have the most ride demand?',
        businessContext: `Before investing in any city, the Operations team needs to know: **where is our platform already being used the most?** High existing demand validates that a market is active and riders are engaging with the product. Let's start by counting completed trips per city.`,
        teach: `Join \`trips\` to \`drivers\` on \`driver_id\` to attach the \`city\` dimension to trip records. Filter for \`completed = true\` so cancelled or incomplete trips don't inflate the numbers. Count \`trip_id\` per city and view as a **Bar** chart.`,
        explanation: `The bar chart immediately surfaces which cities are already our strongest markets. Before expanding anywhere new, we need to know where the platform has already proven product-market fit - high completed trip volume is the clearest signal.`,
        question: 'Create a bar chart showing total completed trips per city.',
        goal: 'Bar chart showing total completed trips per city.',
        hint: `City lives on the \`drivers\` table, so you need a JOIN to group trips by city.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`city\`.
5. Set the Metric to **COUNT** of \`trip_id\`.
6. Run and select **Bar** chart. Use **Sort** to rank cities highest-to-lowest.`,
        expectedChartType: 'bar',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'COUNT', column: 'trip_id' },
            groupBy: [{ column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'demand-growth-by-city',
        level: 1,
        title: 'Q2: Is demand growing or shrinking in each city?',
        businessContext: `High current volume is encouraging - but we need to know if it's *growing*. **A city with stable high demand is a solid market; a city showing rapid growth is a strategic opportunity.** Let's plot monthly trip volume per city over our full two-year dataset to spot trends.`,
        teach: `Group by \`trip_date\` at **Month** granularity and also by \`city\` to get one trend line per city. An **Area** chart stacks the cities visually, making relative growth easy to compare over time.`,
        explanation: `Growth trajectory matters as much as current volume. A city that is accelerating month-over-month is a far better expansion target than one that has plateaued - even if the plateau is at a higher absolute level. The area chart makes this trajectory comparison instant.`,
        question: 'Create an area chart showing monthly trip volume broken down by city.',
        goal: 'Area chart showing monthly completed trip volume per city over time.',
        hint: `Two Group By dimensions gives you one series per city plotted over time.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`trip_date\` (granularity: **Month**).
5. Add a second Group By for \`city\`.
6. Set the Metric to **COUNT** of \`trip_id\`.
7. Run and select **Area** chart.`,
        expectedChartType: 'area',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'COUNT', column: 'trip_id' },
            groupBy: [{ column: 'trip_date', granularity: 'month' }, { column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'peak-hours-by-city',
        level: 1,
        title: 'Q3: Which hours of the day are busiest - and does it vary by city?',
        businessContext: `Driver scheduling is one of the biggest operational costs. **If we know that Berlin peaks at 8am and São Paulo peaks at 11pm, we need city-specific staffing plans - not one-size-fits-all hours.** A heatmap of city × hour shows demand intensity across two dimensions at a glance, far more actionable than a single city average.`,
        teach: `Group by both \`city\` (from the drivers table) and \`trip_hour\` to get a COUNT for every city-hour combination. The **Heatmap** chart turns this into a colour-coded grid: rows = cities, columns = hours 0-23, orange = high demand, blue/white = low demand. Two peaks (morning commute + evening) should appear as bright columns across most cities.`,
        explanation: `If all cities share the same peak hours we can run one global staffing model. If they differ significantly, each city needs a tailored schedule. Cities with strong late-night demand (e.g. peaks at 11pm-1am) may need a nightlife-focused driver incentive programme, while cities that peak at commute times benefit from morning and evening surge pricing.`,
        question: 'Create a heatmap showing trip volume broken down by city and hour of day.',
        goal: 'Heatmap with city on the Y-axis and trip_hour (0-23) on the X-axis, coloured by trip count.',
        hint: `Group by TWO dimensions (city + trip_hour) to populate every cell in the grid.
1. Select \`trips\` and **Join** \`drivers\` on \`driver_id\`.
2. Filter: \`completed\` = **true**.
3. Add Group By: \`city\`.
4. Add a second Group By: \`trip_hour\`.
5. Metric: **COUNT** of \`trip_id\`.
6. Run → select **Heatmap** chart.
7. Bright orange cells = peak demand hours. Scan across rows to compare cities.`,
        expectedChartType: 'heatmap',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'COUNT', column: 'trip_id' },
            groupBy: [{ column: 'city' }, { column: 'trip_hour' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    // ═══ Chapter 2 - Revenue Analysis ════════════════════════════════
    {
        id: 'total-revenue-by-city',
        level: 2,
        title: 'Q4: Which cities generate the most total revenue?',
        businessContext: `Volume tells us where demand is - but revenue tells us where the *money* is. **A city with fewer trips but higher-value fares could outperform a busier city on revenue.** Let's now look at total fare amounts by city to understand financial scale.`,
        teach: `Sum \`fare_amount\` grouped by \`city\`. This is the same structure as Q1 but switching the aggregation from COUNT to SUM - a simple change that shifts the question from "how many?" to "how much?"`,
        explanation: `Comparing the revenue ranking to the trip volume ranking from Q1 is the key insight here. Cities that maintain their rank (high volume = high revenue) have stable unit economics. Cities that drop in rank when switching to revenue are doing lots of low-value trips - a warning sign for expansion profitability.`,
        question: 'Create a bar chart showing total fare revenue per city.',
        goal: 'Bar chart showing SUM of fare_amount per city for completed trips.',
        hint: `Same setup as Q1 - just change the Metric from COUNT to SUM.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`city\`.
5. Set the Metric to **SUM** of \`fare_amount\`.
6. Run and select **Bar** chart.
7. Compare the rankings to Q1 - do the same cities dominate?`,
        expectedChartType: 'bar',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'SUM', column: 'fare_amount' },
            groupBy: [{ column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'driver-volume-vs-earnings',
        level: 2,
        title: 'Q5: Do high-volume drivers earn more per trip?',
        businessContext: `Some drivers are high-volume specialists, completing dozens of trips a day. Others prefer fewer but higher-value rides. **Does trip volume correlate with average fare? Are busy drivers also well-paid drivers, or do they sacrifice price for quantity?** A scatter plot with one dot per driver reveals this trade-off directly.`,
        teach: `Group by \`driver_id\` to get one dot per driver. Add **COUNT(trip_id)** as one metric and **AVG(fare_amount)** as another - this gives total trips and average earnings side by side. The **Scatter** chart will map the two numeric columns onto X and Y axes automatically.`,
        explanation: `If high-volume drivers cluster at low average fares, it suggests they are taking many short, cheap rides - burning time for low yield. If there's no clear correlation, drivers are working differently and a segmentation strategy (premium vs volume) could help optimise driver earnings and platform margin simultaneously.`,
        question: 'Create a scatter plot of total trips vs average fare amount, with one dot per driver.',
        goal: 'Scatter plot with one dot per driver: COUNT of trips vs AVG fare amount.',
        hint: `Aggregate at the driver level so each driver becomes a single data point.
1. Select the \`trips\` table.
2. Add a **Filter**: \`completed\` = **true**.
3. Add a Group By for \`driver_id\`.
4. Add two Metrics: **COUNT** of \`trip_id\` and **AVG** of \`fare_amount\`.
5. Run and select **Scatter** chart.
6. Set X axis to \`count_trip_id\` and Y to \`avg_fare_amount\`.`,
        expectedChartType: 'scatter',
        answer: {
            table: 'trips',
            join: null,
            metrics: [
                { fn: 'COUNT', column: 'trip_id' },
                { fn: 'AVG', column: 'fare_amount' },
            ],
            groupBy: [{ column: 'driver_id' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'fare-distribution-by-city',
        level: 2,
        title: 'Q6: How does fare distribution vary between cities in January 2025?',
        businessContext: `Average fare hides a lot. **A city with a $20 average might have a tight cluster around $20 - or it might have half its trips at $8 and half at $32.** The full distribution shape matters for pricing strategy and rider experience. A box plot reveals the spread, typical range, and outliers of fare amounts across every city - without flattening the data into a single number.`,
        teach: `With box plots, you don't need to compute statistics yourself. Just return **one row per trip** with the category (city) and the value (fare_amount) - group by \`trip_id\` + \`city\` with a MAX metric. The chart groups those rows by city and computes the distribution automatically. The coloured box covers the middle 50% of fares; the badge shows the median; the dashed lines reach the min and max.`,
        explanation: `A narrow box means predictable, consistent fares - great for rider experience and pricing clarity. A wide box with a long upper whisker means the city has high-fare outliers: long airport runs or surge events that drive up the average. A city with a high median and tight distribution is the ideal expansion target for fare predictability.`,
        question: 'Create a box plot showing the distribution of fare amounts per city for completed trips in January 2025.',
        goal: 'Box plot with one box per city showing fare distribution for Jan 2025.',
        hint: `Return one row per trip so each fare value is preserved - then the chart computes the distribution.
1. Select the \`trips\` table.
2. Add **Filters**: \`completed\` = **true**, \`trip_date\` >= **2025-01-01**, \`trip_date\` <= **2025-01-31**.
3. Add a Group By for **both** \`trip_id\` and \`city\` (trip_id first).
4. Add one Metric: **MAX** of \`fare_amount\`.
5. Hit **Visualize**.
6. Select the **Box** chart type.
7. Set X axis to \`city\` and Y axis to \`max_fare_amount\`.
Boxes sorted by median - tallest median = highest-earning market.`,
        expectedChartType: 'boxplot',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'MAX', column: 'fare_amount' },
            groupBy: [{ column: 'trip_id' }, { column: 'city' }],
            filters: [
                { column: 'completed', operator: '=', value: 'true' },
                { column: 'trip_date', operator: '>=', value: '2025-01-01' },
                { column: 'trip_date', operator: '<=', value: '2025-01-31' },
            ],
        },
    },

    // ═══ Chapter 3 - Efficiency & Synthesis ═══════════════════════════════════
    {
        id: 'avg-fare-by-city',
        level: 3,
        title: 'Q7: Which cities command the highest average fare per trip?',
        businessContext: `Average fare per trip is a cleaner measure of market quality than total revenue - it's not inflated by trip volume. **A high average fare suggests either longer trips, premium demand, or less price-sensitive riders.** All three are positive signals for expansion profitability.`,
        teach: `This is similar to Q4 but with AVG instead of SUM. The ranking of cities may shift substantially - markets that ranked lower on total revenue could rank much higher on per-trip value.`,
        explanation: `Average fare strips out the volume effect and gives a true measure of revenue quality per trip. A city that ranked mid-table on total revenue but tops here is a high-value market that's simply underserved - its unit economics are strong, it just needs more driver supply to scale.`,
        question: 'Create a bar chart showing average fare per trip by city.',
        goal: 'Bar chart showing AVG(fare_amount) per city for completed trips.',
        hint: `Same structure as Q4 - just swap SUM for AVG.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`city\`.
5. Set the Metric to **AVG** of \`fare_amount\`.
6. Run and select **Bar** chart.
7. Sort descending. Compare the order to Q4 (total revenue).`,
        expectedChartType: 'bar',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            aggregation: { fn: 'AVG', column: 'fare_amount' },
            groupBy: [{ column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'driver-rating-vs-earnings',
        level: 3,
        title: 'Q8: Do higher-rated drivers earn more per trip?',
        businessContext: `Driver rating reflects service quality - but does better service translate to higher fares? **If it does, incentivising quality improvements has a direct revenue return. If it doesn't, we need to rethink how we measure driver performance.** Let's plot each driver's average rating against their average fare to find out.`,
        teach: `Group by \`driver_id\` to create one data point per driver. Use **AVG(rating)** and **AVG(fare_amount)** as two metrics. The **Scatter** chart will plot one dot per driver - if there's an upward pattern, quality correlates with earnings.`,
        explanation: `If rating and fare are uncorrelated, high-rated drivers aren't financially rewarded for their service quality - they need a different incentive structure. A positive correlation justifies investing in driver quality programmes, since better-rated drivers will naturally attract longer or more premium trips.`,
        question: 'Create a scatter plot of driver rating vs average fare per trip.',
        goal: 'Scatter plot with one dot per driver: AVG rating vs AVG fare.',
        hint: `Join so you can access both the driver's \`rating\` and their trip \`fare_amount\` in one query.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`driver_id\`.
5. Add two Metrics: **AVG** of \`rating\` and **AVG** of \`fare_amount\`.
6. Run and select **Scatter** chart.
7. Set X axis to \`avg_rating\` and Y to \`avg_fare_amount\`.`,
        expectedChartType: 'scatter',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            metrics: [
                { fn: 'AVG', column: 'rating' },
                { fn: 'AVG', column: 'fare_amount' },
            ],
            groupBy: [{ column: 'driver_id' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'revenue-per-km-by-city',
        level: 3,
        title: 'Q9: Which cities are most efficient - revenue per km driven?',
        businessContext: `Total fare doesn't account for how hard drivers are working to earn it. **Revenue per km tells us how efficiently each market converts road time into money.** A city where drivers earn more per km is operationally more attractive - it means shorter trips generating comparable revenue, or denser urban routes with less dead mileage.`,
        teach: `This requires a **Custom Expression**: \`SUM(fare_amount) / SUM(trip_distance_km)\`. This calculates aggregate revenue divided by aggregate kilometres - giving the true revenue-per-km rate across all trips in each city.`,
        explanation: `Revenue per km is the operational efficiency metric. A city where drivers earn more per km means shorter, denser routes - less dead mileage, faster turnarounds, and more trips per hour. Cities with low revenue per km may have structural reasons (sprawling geography, long motorway trips) or fixable ones (poor route matching, weak demand in certain zones).`,
        question: 'Create a bar chart showing revenue per kilometre for each city.',
        goal: 'Bar chart showing SUM(fare_amount) / SUM(trip_distance_km) per city.',
        hint: `The tool supports custom expressions - type the formula directly into the Metric field.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`city\`.
5. Set the Metric to **Custom Expression**: \`SUM(fare_amount) / SUM(trip_distance_km)\`
6. Run and select **Bar** chart. Sort descending to rank cities by efficiency.`,
        expectedChartType: 'bar',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            metrics: [
                { fn: 'CUSTOM', expression: 'SUM(fare_amount) / SUM(trip_distance_km)', alias: 'revenue_per_km' },
            ],
            groupBy: [{ column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },

    {
        id: 'expansion-target-cities',
        level: 3,
        title: 'Q10: Which cities should we expand into?',
        businessContext: `We've now analysed demand, revenue, and efficiency across all cities. The final step is to **synthesise these signals into a single view** that supports an expansion recommendation. A city that scores well across all three dimensions - high volume, strong average fare, and strong revenue-per-km - is the clearest candidate for investment.`,
        teach: `Use a **Combo** chart with SUM(fare_amount) as bars (total revenue scale) and AVG(fare_amount) as a line (per-trip quality). This single chart captures both *how big* a market is and *how valuable* each trip is - the two key expansion signals. Compare the result to what you found in Q1 and Q9 to complete the picture.`,
        explanation: `The ideal expansion city sits at the top right of both signals: tall bar (large total revenue) and high line value (strong average fare per trip). Cities with tall bars but low lines are volume markets with thin margins. Cities with high lines but short bars are premium but underdeveloped - potentially the highest-upside targets if demand can be scaled.`,
        question: 'Create a combo chart showing total revenue (bar) and average fare per trip (line) by city.',
        goal: 'Combo chart comparing total revenue and average fare per trip across cities.',
        hint: `A Combo chart overlays a bar series and a line series on shared axes - perfect for comparing absolute scale with per-unit quality.
1. Select the \`trips\` table.
2. Add a **Join** with \`drivers\` on \`driver_id\`.
3. Add a **Filter**: \`completed\` = **true**.
4. Add a Group By for \`city\`.
5. Add two Metrics: **SUM** of \`fare_amount\` AND **AVG** of \`fare_amount\`.
6. Run and select **Combo** chart.
7. Reflect: which cities score high on both bars AND line? Those are your expansion targets.`,
        expectedChartType: 'combo',
        answer: {
            table: 'trips',
            join: { table: 'drivers', type: 'JOIN', leftCol: 'driver_id', rightCol: 'driver_id' },
            metrics: [
                { fn: 'SUM', column: 'fare_amount' },
                { fn: 'AVG', column: 'fare_amount' },
            ],
            groupBy: [{ column: 'city' }],
            filters: [{ column: 'completed', operator: '=', value: 'true' }],
        },
    },
];

/**
 * Validate user's query state against the expected answer
 * Returns { passed: boolean, feedback: string }
 */
export function validateAnswer(userState, challenge) {
    const expected = challenge.answer;
    const issues = [];

    if (userState.table !== expected.table) {
        issues.push(`Start with the "${expected.table}" table.`);
    }

    if (expected.join) {
        const hasJoin = (userState.join && userState.join.table === expected.join.table)
            || (userState.joins && userState.joins.some(j => j.table === expected.join.table));
        if (!hasJoin) {
            issues.push(`You need to join with the "${expected.join.table}" table on driver_id.`);
        }
    }

    const expectedMetrics = expected.metrics
        ? expected.metrics
        : expected.aggregation ? [expected.aggregation] : [];

    if (expectedMetrics.length > 0) {
        const metricsToCheck = (userState.metrics && userState.metrics.length > 0)
            ? userState.metrics
            : (userState.aggregation ? [userState.aggregation] : []);
        const firstExpected = expectedMetrics[0];
        const match = metricsToCheck.find(m => m.fn === firstExpected.fn);
        if (!match) {
            const label = firstExpected.fn === 'CUSTOM' ? 'a Custom Expression' : `${firstExpected.fn}`;
            issues.push(`Use ${label} as at least one of your metrics.`);
        }
    }

    if (expected.groupBy && expected.groupBy.length > 0) {
        const expectedCols = expected.groupBy.map(g => g.column).sort();
        const userCols = (userState.groupBy || []).map(g => g.column).sort();
        if (JSON.stringify(expectedCols) !== JSON.stringify(userCols)) {
            issues.push(`Group by: expected [${expectedCols.join(', ')}], got [${userCols.join(', ')}].`);
        }
    }

    if (issues.length > 0) {
        return { passed: false, feedback: issues[0] };
    }

    return { passed: true, feedback: 'Great work - the data is visualised correctly!' };
}
